import { DeleteOutlined, DragOutlined, ExportOutlined } from '@ant-design/icons';
import { Button, Radio, Tag, Tooltip } from 'antd';
import { useSortable } from '@dnd-kit/sortable';

import { mutedNoteStyle } from '../muted-text-style';

const MAIN_MEMBER_HINT = 'Takdim cümlesi, farklı dildeki madde adı ve kimlik kartı bu maddeden alınır.';

const stopDragStart = (e) => e.stopPropagation();

/**
 * @param {Object} props
 * @param {number} props.id - dnd-kit item id (Form.List field key)
 * @param {string} props.label - Madde başlığı
 * @param {number} props.articleId - Madde detay linki için numeric id
 * @param {Function} props.onRemove - Silme callback
 * @param {boolean} [props.readOnly=false] - readOnly modda drag handle ve sil butonu gizlenir
 * @param {boolean} [props.showMainMember=false] - Ana madde seçimi ve rozeti gösterilsin mi (feature flag)
 * @param {boolean} [props.isMain=false] - Satır grubun ana maddesi mi
 * @param {Function} [props.onSelectMain] - Satırı ana madde yapma callback
 * @param {string | null} [props.statusLabel=null] - Madde teklifinin statü etiketi; yoksa gösterilmez.
 *   Ana madde satırında rozet yer kapladığı için rozetin ipucunda görünür.
 * @param {string} [props.error] - Validation hata mesajı
 */
function SortableItem({
  id,
  label,
  articleId,
  onRemove,
  readOnly = false,
  showMainMember = false,
  isMain = false,
  onSelectMain,
  statusLabel = null,
  error,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const verticalTransform = transform ? `translateY(${transform.y}px)` : undefined;
  const isHighlighted = showMainMember && isMain;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: verticalTransform,
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <div
        style={{
          borderRadius: '6px',
          cursor: readOnly ? 'default' : 'grab',
          backgroundColor: isHighlighted ? '#eaf5fc' : 'white',
          ...(isHighlighted && { borderColor: '#3da4e6' }),
        }}
        className="gx-d-flex gx-align-items-center gx-justify-content-between gx-mb-1 gx-border gx-p-1"
        {...(readOnly ? {} : { ...attributes, ...listeners })}
      >
        <div className="gx-d-flex gx-align-items-center" style={{ minWidth: 0 }}>
          {showMainMember && (
            <Tooltip title={MAIN_MEMBER_HINT}>
              <span
                className="gx-d-flex gx-align-items-center gx-ml-1"
                onPointerDown={stopDragStart}
                onMouseDown={stopDragStart}
              >
                <Radio className="gx-mr-0" checked={isMain} disabled={readOnly} onChange={onSelectMain} />
              </span>
            </Tooltip>
          )}
          {!readOnly && (
            <Button
              size="small"
              type="text"
              className="gx-m-0 gx-mr-1 gx-d-flex gx-align-items-center gx-justify-content-center"
              icon={<DragOutlined />}
            />
          )}
          <div style={{ transform: 'translateY(1.5px)' }}>{label}</div>
        </div>
        <div className="gx-d-flex gx-align-items-center" style={{ gap: 4 }}>
          {isHighlighted ? (
            <Tooltip title={statusLabel}>
              <Tag color="blue" className="gx-m-0 gx-mr-1">
                Ana madde
              </Tag>
            </Tooltip>
          ) : (
            statusLabel && (
              <span className="gx-mr-1" style={mutedNoteStyle}>
                {statusLabel}
              </span>
            )
          )}
          <a
            href={`/madde-detay/${articleId}`}
            target="_blank"
            rel="noopener noreferrer"
            onPointerDown={stopDragStart}
            onMouseDown={stopDragStart}
            onClick={stopDragStart}
          >
            <Button
              size="small"
              type="link"
              className="gx-mb-0 gx-d-flex gx-align-items-center gx-justify-content-center"
              icon={<ExportOutlined style={{ fontSize: 14 }} />}
            />
          </a>
          {!readOnly && (
            <Button
              size="small"
              className="gx-mb-0 gx-d-flex gx-align-items-center gx-justify-content-center"
              icon={<DeleteOutlined />}
              onPointerDown={stopDragStart}
              onMouseDown={stopDragStart}
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              danger
            />
          )}
        </div>
      </div>
      {error && (
        <div
          className="gx-mb-2"
          style={{ color: '#f5222d', fontSize: '12px', paddingLeft: '4px' }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default SortableItem;
